import {
  Box,
  Button,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import AsyncSelectScroll from "@/components/AsyncSelectScroll";
import Flex from "../../../../../components/Flex";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import SideSheetDrawer from "../../../../../components/SidesheetDrawer";
import Text from "../../../../../components/Text";
import { useDebounce } from "../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../hooks/use-disclosure";
import { useSnackbar } from "../../../../../hooks/useSnackbar";
import { useRosTemplateById } from "../../../../../queries/template/new-flow/use-new-template-by-id-list";
import { useNewTemplateList } from "../../../../../queries/template/new-flow/use-new-template-list";
import { GetAllClaimTemplateTypesEnum } from "../../../../../services";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "../../../../../utils/utils";
import * as Yup from "yup";
import { isEmpty } from "lodash";
import PatientSelectScroll from "@/components/PatientSelectScroll";
import RichTextEditor from "../../../../../components/richTextEditor/RichTextEditor";
import { EmailTo } from "../email/EmailTo";
import { useSendSmsToPatient } from "../../../../../queries/patient/use-patient-send-sms-to-patient";
import { SmsConsentInfo } from "./ConsentInfoBanner";

interface SendBulkSMSModalProps {
  disClosureHost: DisclosureHost;
  disableAddPatient?: boolean;
  onSave?: () => void;
}

type SmsType = "single" | "bulk";

const SendBulkSMSModal = ({
  disClosureHost,
  disableAddPatient = false,
  onSave,
}: SendBulkSMSModalProps) => {
  const [smsType, setSmsType] = useState<SmsType>("single");
  const [selectedTemplate, setSelectedTemplate] = useState<any>();
  const [searchTemplate, setSearchTemplate] = useDebounce<string>("");
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const {
    data: templateList,
    isLoading,
    isRefetching,
    isFetching,
  } = useNewTemplateList({
    pagination,
    searchKey: searchTemplate,
    templateType: GetAllClaimTemplateTypesEnum.Sms,
    xTENANTID: getXtenantId(),
  });

  const [patients, setPatients] = useState<{ uuid: string; name: string }[]>(
    disClosureHost?.data ?? []
  );
  const { mutateAsync: sendSMS } = useSendSmsToPatient();

  const { data: selectedTemplateData, isLoading: selectedTemplateLoading } =
    useRosTemplateById({
      rosTemplateUuid: selectedTemplate?.value,
      xTENANTID: getXtenantId(),
    });

  const showSnackbar = useSnackbar();

  const formik = useFormik({
    initialValues: { message: "" },
    enableReinitialize: true,
    validationSchema: Yup.object({
      message: Yup.string()
        .required()
        .label("Message")
        .max(1200, "Message must be 160 characters or less"),
    }),
    onSubmit: (v: any) => {
      if (isEmpty(patients)) {
        showSnackbar({
          type: "error",
          message: "Please add at least one patient",
        });
        return;
      }
      const payload = {
        emailToPatient: {
          description: v?.message,
          patientUuids: patients?.map((it) => it?.uuid),
        },
        xTENANTID: getXtenantId(),
      };
      sendSMS(payload)
        .then((res) => {
          showSnackbar(getSuccessSnackbar(res));
          onSave?.();
          closeAndReset();
        })
        .catch((err) => showSnackbar(getErrorSnackbar(err)));
    },
  });

  useEffect(() => setPatients(disClosureHost?.data), [disClosureHost?.data]);

  useEffect(() => {
    if (selectedTemplateData?.data && selectedTemplate?.value) {
      formik.setFieldValue("message", selectedTemplateData?.data?.description);
    }
  }, [selectedTemplateData?.data, selectedTemplate?.value]);

  const prevOpenedRef = useRef(disClosureHost.opened);
  useEffect(() => {
    const wasOpen = prevOpenedRef.current;
    prevOpenedRef.current = disClosureHost.opened;
    if (wasOpen && !disClosureHost.opened) {
      setSelectedTemplate(undefined);
      setSelectedPatient(null);
      setPatients([]);
      formik.resetForm();
    }
  }, [disClosureHost.opened]);

  const handleRemovePatient = (ind: number) => {
    setPatients((prev) => prev?.toSpliced(ind, 1));
  };

  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const handleAddPatient = (patient: any) => {
    setSelectedPatient(patient);
    if (patients?.some((it) => it?.uuid === patient?.value)) {
      showSnackbar({
        type: "error",
        message: "Patient already added",
      });
      setSelectedPatient(null);
      return;
    } else {
      setPatients((prev) => {
        return [
          ...(prev ?? []),
          { uuid: patient?.value, name: patient?.label },
        ];
      });
    }

    setTimeout(() => setSelectedPatient(null), 0);
  };

  const closeAndReset = () => {
    setSelectedTemplate(undefined);
    setSelectedPatient(null);
    setPatients([]);
    formik.resetForm();
    disClosureHost.close();
  };

  return (
    <SideSheetDrawer
      open={disClosureHost.opened}
      w="872px"
      title="New SMS"
      onClose={closeAndReset}
      footerActions={
        <>
          <Button variant="outlined" sx={{ mr: 1 }} onClick={closeAndReset}>
            Cancel
          </Button>
          <Button variant="contained" onClick={formik.submitForm}>
            Send
          </Button>
        </>
      }
    >
      <LoadingOverlay isLoading={selectedTemplateLoading} />

      <Flex flexDirection={"column"}>
        <Divider sx={{ marginBottom: 2, borderColor: "gray" }} />
        <Flex flexDirection="column" gap={1.5} sx={{ marginBottom: 2 }}>
          <Text color="#1A1A1A66">SMS Type</Text>
          <SmsTypeToggle
            value={smsType}
            onChange={(v) => {
              setSmsType(v as SmsType);
              setSelectedPatient(null);
              setPatients([]);
            }}
            options={[
              { value: "single", label: "Single" },
              { value: "bulk", label: "Bulk" },
            ]}
          />
        </Flex>
        <Flex width={"100%"}>
          {smsType === "single" ? (
            <Box width={300}>
              <PatientSelectScroll
                placeholder="Select Patient"
                value={
                  patients?.length === 1 && patients[0]
                    ? { value: patients[0].uuid, label: patients[0].name }
                    : null
                }
                onChange={(val) =>
                  setPatients(val ? [{ uuid: val.value, name: val.label }] : [])
                }
              />
            </Box>
          ) : (
            <EmailTo
              patientList={patients ?? []}
              handleRemovePatient={handleRemovePatient}
              disableAddPatient={disableAddPatient}
              selectedPatient={selectedPatient}
              handleAddPatient={handleAddPatient}
            />
          )}
        </Flex>

        <Divider sx={{ marginY: 2, borderColor: "gray" }} />
        <AsyncSelectScroll
          loading={isLoading || isRefetching || isFetching}
          placeholder="Select SMS Template"
          onSearch={(v) => {
            setSearchTemplate(v);
            setPagination((p) => ({ ...p, page: 0 }));
          }}
          options={templateList?.data?.content?.map((it) => ({
            value: it?.uuid,
            label: it?.templateName,
          }))}
          value={selectedTemplate}
          onChange={(val) => setSelectedTemplate(val)}
          pagination={pagination}
          setPagination={setPagination}
          totalElements={templateList?.data?.page?.totalElements}
        />
        <Divider sx={{ marginY: 2, borderColor: "gray" }} />
        <RichTextEditor
          name="message"
          value={formik.values?.message}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched?.message && (formik.errors?.message as any)}
          placeholder="Enter Message"
          minRows={16}
        />
        <SmsConsentInfo sx={{ mt: 2 }} />
      </Flex>
    </SideSheetDrawer>
  );
};

interface SmsTypeToggleOption {
  value: string;
  label: string;
}

interface SmsTypeToggleProps {
  value: string;
  onChange: (value: string) => void;
  options: SmsTypeToggleOption[];
}

const SmsTypeToggle = ({ value, onChange, options }: SmsTypeToggleProps) => (
  <ToggleButtonGroup
    value={value}
    exclusive
    onChange={(_, v) => v !== null && onChange(v)}
    aria-label="SMS type"
    sx={{
      width: "fit-content",
      borderRadius: "8px",
      backgroundColor: "#E8E8E8",
      padding: "4px",
      "& .MuiToggleButtonGroup-grouped": {
        border: "none",
        borderRadius: "6px",
      },
      "& .MuiToggleButton-root": {
        textTransform: "none",
        minWidth: 80,
        py: 0.75,
        color: "#1A1A1A",
        backgroundColor: "transparent",
        "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
      },
      "& .MuiToggleButton-root.Mui-selected": {
        color: "#054FA4",
        backgroundColor: "#fff",
        "&:hover": { backgroundColor: "#fff" },
      },
    }}
  >
    {options.map((opt) => (
      <ToggleButton key={opt.value} value={opt.value}>
        {opt.label}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

export default SendBulkSMSModal;
